/**
 * Motor de tours del puente del host (A4-iris, ADR 0003, refs loomitz/iris#24).
 *
 * Vive del lado HOST (lo carga `bridge.js`): es la capacidad que en A3 quedó
 * REGISTRADA pero respondiendo `capacidad_no_configurada` (seam limpio en
 * `nucleo.ts`). Aquí se cablea de verdad.
 *
 * Responsabilidades:
 *   1. CARGA del catálogo local del sitio (`ConfigHost.toursUrl`, same-origin):
 *      `cargarCatalogoTours` hace el fetch y `validarCatalogoTours` valida el
 *      JSON contra el shape del contrato (`$defs/CatalogoTours`+`Tour`+`PasoTour`)
 *      — FAIL-CLOSED: cualquier violación estructural (ancla que no cumple el
 *      pattern anti-selector, texto ausente/largo, paso fuera de rango, id
 *      duplicado, path malformado) ⇒ `null` ⇒ el bridge NO cablea el motor y los
 *      comandos de tour siguen respondiendo `capacidad_no_configurada`.
 *   2. EJECUCIÓN de pasos sobre anclas `data-iris`: resaltar (overlay/outline SIN
 *      mutar permanentemente los estilos del host), scroll al ancla, tooltip con
 *      el texto del paso pintado con `textContent` (JAMÁS `innerHTML` — cero HTML
 *      del catálogo al DOM), esperar la acción esperada (click en el ancla /
 *      avance manual), avanzar/cancelar. Cuando un auto-avance por click apunta a
 *      un ancla que el host monta DE FORMA ASÍNCRONA (modal de Vue tras un
 *      `await import()`, flush de reactividad), el motor NO se rinde en el mismo
 *      tick: hace una ESPERA ACOTADA (reintenta el `querySelector` unas veces con
 *      intervalo) y sólo entonces emite `ancla_no_encontrada` (tipado + evento).
 *      El match de página normaliza el trailing slash (`/fineart` ≡ `/fineart/`).
 *      Página que no aplica → `pagina_no_aplica` (detalle estructurado).
 *   3. EVENTOS `tour_evento` hacia el shell (`paso_mostrado`, `paso_completado`,
 *      `tour_terminado`, `cancelado_por_usuario`, `ancla_no_encontrada`).
 *
 * El motor es DOM-consciente pero con seams inyectables (documento, reloj de
 * eventos, efecto de resaltado) para el harness de dos extremos sin navegador.
 * `nucleo.ts` (puro) delega en la interfaz {@link MotorTours} y envuelve el
 * {@link ResultadoTour} en la Respuesta tipada del sobre.
 */
import type {
  AccionEsperada,
  AnclaResaltarPayload,
  CatalogoTours,
  ErrorPuenteDetalle,
  PasoTour,
  Tour,
  TourAvanzarPayload,
  TourCancelarPayload,
  TourCorrerPayload,
  TourEventoPayload,
  TourId,
} from "./tipos"

// --- Patterns del contrato (espejo de `tools/puente-schemas.json`) ----------

/** `$defs/TourId`. */
const RE_TOUR_ID = /^[a-z0-9-]+\.[a-z0-9][a-z0-9.-]*$/
/** `$defs/AnclaIris` — ES el guard anti-selector (sin `.#[]>:`, sin espacios). */
const RE_ANCLA = /^[a-z0-9]+(-[a-z0-9]+)*$/
/** `$defs/PaginaPath` — pathname SOLO (sin query/hash/protocol-relative). */
const RE_PAGINA = /^\/(?!\/)[a-zA-Z0-9/_.:-]*$/
/** `$defs/ConfigHost.toursUrl` — path relativo MISMO ORIGEN. */
const RE_TOURS_URL = /^\/(?!\/)[a-zA-Z0-9/._-]*$/
/** `$defs/AccionEsperada.tipo` (enum). */
const ACCIONES_ESPERADAS = new Set(["click", "input", "navegacion", "ninguna"])

// --- Resultado de una operación del motor (lo envuelve `nucleo.ts`) ---------

/**
 * Resultado NEUTRO de una operación del motor: éxito con payload de respuesta o
 * fallo con un código del contrato (`$defs/CodigosConocidos`) + detalle
 * estructurado. `nucleo.ts` lo traduce a `respuestaOk`/`respuestaError` con el
 * `id`/`comando` del sobre — el motor no conoce el envelope del wire.
 */
export type ResultadoTour =
  | { ok: true; payload: object }
  | { ok: false; codigo: string; detalle?: ErrorPuenteDetalle }

/** Capacidad de tours que consume `DespachoCtx` (fail-closed si ausente). */
export interface MotorTours {
  correr(payload: TourCorrerPayload): ResultadoTour
  avanzar(payload: TourAvanzarPayload): ResultadoTour
  cancelar(payload: TourCancelarPayload): ResultadoTour
  resaltar(payload: AnclaResaltarPayload): ResultadoTour
  /** Ids VIVOS del catálogo (lo que el bridge anuncia en `hola_puente.tours`). */
  tours(): TourId[]
  /** Limpia el resaltado activo y listeners (teardown del bridge). */
  destruir(): void
}

// --- Validación FAIL-CLOSED del catálogo ------------------------------------

function esObjeto(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x)
}
function esStrEntre(x: unknown, min: number, max: number): x is string {
  return typeof x === "string" && x.length >= min && x.length <= max
}
function esEnteroDesde(x: unknown, min: number): x is number {
  return typeof x === "number" && Number.isInteger(x) && x >= min
}
function esPaginaPath(x: unknown): x is string {
  return typeof x === "string" && x.length <= 200 && RE_PAGINA.test(x)
}

/**
 * Normaliza un pathname para comparar aplicabilidad de página TOLERANDO el
 * trailing slash: `/fineart` y `/fineart/` (ambas 200 sin redirect canónico en
 * print) casan igual; la raíz `/` se preserva. El catálogo puede declarar la
 * página en cualquiera de las dos formas y el tráfico orgánico (sitemap con
 * slash) sigue matcheando. Sólo colapsa slashes finales — no toca query/hash
 * (que el guard de `PaginaPath` ya prohíbe).
 */
export function normalizarPagina(p: string): string {
  const sinSlashFinal = p.replace(/\/+$/, "")
  return sinSlashFinal === "" ? "/" : sinSlashFinal
}

/**
 * Valida una `AccionEsperada`. RUNTIME TOLERANTE (regla del contrato): un `tipo`
 * desconocido en un catálogo más nuevo degrada a `ninguna` (paso manual) con
 * aviso, para que un catálogo nuevo no rompa un bridge cacheado viejo. Un
 * `pagina` presente pero malformado (path/PII) SÍ es fatal (fail-closed en
 * paths). `null` (fatal) sólo si el shape base no es objeto o `pagina` inválido.
 */
function normalizarAccionEsperada(
  raw: unknown,
  onAviso: (m: string) => void
): AccionEsperada | null | undefined {
  if (raw === undefined) return undefined
  if (!esObjeto(raw)) return null
  const tipoCrudo = raw.tipo
  let tipo: AccionEsperada["tipo"] = "ninguna"
  if (ACCIONES_ESPERADAS.has(tipoCrudo as string)) {
    tipo = tipoCrudo as AccionEsperada["tipo"]
  } else {
    onAviso(`accionEsperada.tipo desconocido '${String(tipoCrudo)}': degrada a 'ninguna'`)
  }
  const accion: AccionEsperada = { tipo }
  if ("pagina" in raw) {
    if (!esPaginaPath(raw.pagina)) return null
    accion.pagina = raw.pagina
  }
  return accion
}

/** Valida un paso (`$defs/PasoTour`) → normalizado o `null` (fatal). */
function validarPaso(raw: unknown, onAviso: (m: string) => void): PasoTour | null {
  if (!esObjeto(raw)) return null
  // El pattern de AnclaIris ES el guard anti-selector: un ancla que no cumple
  // hace inválido TODO el catálogo (fail-closed) — jamás llega un selector al DOM.
  if (!esStrEntre(raw.ancla, 1, 64) || !RE_ANCLA.test(raw.ancla)) return null
  if (!esStrEntre(raw.texto, 1, 280)) return null
  const accionEsperada = normalizarAccionEsperada(raw.accionEsperada, onAviso)
  if (accionEsperada === null) return null
  const paso: PasoTour = { ancla: raw.ancla, texto: raw.texto }
  if (accionEsperada) paso.accionEsperada = accionEsperada
  return paso
}

/** Valida un tour (`$defs/Tour`) → normalizado o `null` (fatal). */
export function validarTour(raw: unknown, onAviso: (m: string) => void = () => {}): Tour | null {
  if (!esObjeto(raw)) return null
  if (!esStrEntre(raw.id, 1, 64) || !RE_TOUR_ID.test(raw.id)) return null
  if (!esStrEntre(raw.titulo, 1, 80)) return null
  if (!esStrEntre(raw.descripcion, 1, 200)) return null
  if (!esEnteroDesde(raw.version, 1)) return null

  let paginas: string[] | undefined
  if ("paginas" in raw && raw.paginas !== undefined) {
    if (!Array.isArray(raw.paginas) || raw.paginas.length === 0) return null
    const vistas = new Set<string>()
    for (const p of raw.paginas) {
      if (!esPaginaPath(p) || vistas.has(p)) return null
      vistas.add(p)
    }
    paginas = [...vistas]
  }

  if (!Array.isArray(raw.pasos) || raw.pasos.length < 1 || raw.pasos.length > 30) return null
  const pasos: PasoTour[] = []
  for (const crudo of raw.pasos) {
    const paso = validarPaso(crudo, onAviso)
    if (!paso) return null
    pasos.push(paso)
  }

  const tour: Tour = {
    id: raw.id,
    titulo: raw.titulo,
    descripcion: raw.descripcion,
    version: raw.version,
    pasos,
  }
  if (paginas) tour.paginas = paginas
  return tour
}

/**
 * Valida un catálogo (`$defs/CatalogoTours`) contra el `sitio` esperado.
 * FAIL-CLOSED (`null`) ante cualquier violación estructural o id duplicado: el
 * bridge entonces NO cablea el motor (los tours quedan `capacidad_no_configurada`).
 */
export function validarCatalogoTours(
  raw: unknown,
  sitio: string,
  onAviso: (m: string) => void = () => {}
): CatalogoTours | null {
  if (!esObjeto(raw)) return null
  if (raw.sitio !== sitio) {
    onAviso(`catálogo de tours: sitio '${String(raw.sitio)}' ≠ '${sitio}' (fail-closed)`)
    return null
  }
  if (!esEnteroDesde(raw.version, 0)) return null
  if (!Array.isArray(raw.tours)) return null

  const tours: Tour[] = []
  const ids = new Set<string>()
  for (const crudo of raw.tours) {
    const tour = validarTour(crudo, onAviso)
    if (!tour) return null
    if (ids.has(tour.id)) {
      onAviso(`catálogo de tours: id duplicado '${tour.id}' (fail-closed)`)
      return null
    }
    ids.add(tour.id)
    tours.push(tour)
  }
  return { sitio, version: raw.version, tours }
}

// --- Carga same-origin del catálogo -----------------------------------------

export interface CargarCatalogoOpts {
  fetchImpl?: typeof fetch
  onAviso?: (m: string) => void
}

/**
 * Carga y valida el catálogo local del sitio desde `toursUrl` (path relativo
 * MISMO ORIGEN). FAIL-CLOSED a `null`: `toursUrl` con protocolo/`//`/absoluta,
 * error de red, status no-2xx, JSON inválido o shape que no valida ⇒ `null`.
 */
export async function cargarCatalogoTours(
  toursUrl: string,
  sitio: string,
  opts: CargarCatalogoOpts = {}
): Promise<CatalogoTours | null> {
  const onAviso = opts.onAviso ?? ((m: string) => console.warn(m))
  if (typeof toursUrl !== "string" || toursUrl.length > 200 || !RE_TOURS_URL.test(toursUrl)) {
    onAviso(`toursUrl '${String(toursUrl)}' no es un path same-origin: no se cargan tours`)
    return null
  }
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch
  if (typeof fetchImpl !== "function") return null
  try {
    const resp = await fetchImpl(toursUrl)
    if (!resp || !resp.ok) {
      // Fail-open SIN silencio: un 404 aquí deja al shell sin tours y el
      // síntoma aguas abajo ("Tour no disponible") no delata la causa.
      onAviso(
        `catálogo de tours '${toursUrl}' respondió ${resp ? resp.status : "sin respuesta"}: sin tours`
      )
      return null
    }
    return validarCatalogoTours(await resp.json(), sitio, onAviso)
  } catch (error) {
    onAviso(
      `catálogo de tours '${toursUrl}' no se pudo cargar (${error instanceof Error ? error.message : "error"}): sin tours`
    )
    return null
  }
}

// --- Seams del DOM ----------------------------------------------------------

/** Ancla del host consultada por `[data-iris="<ancla>"]` (real o fake en tests). */
export interface TourElemento {
  ownerDocument: Document
  getBoundingClientRect?(): { top: number; left: number; width: number; height: number }
  scrollIntoView?(opts?: unknown): void
  addEventListener(type: "click", handler: () => void): void
  removeEventListener(type: "click", handler: () => void): void
}

/** Documento del host (subconjunto: sólo consultamos por `[data-iris]`). */
export interface TourDocumento {
  querySelector(selector: string): TourElemento | null
}

/** Efecto de resaltado: pinta overlay+tooltip y devuelve un cleanup. */
export type EfectoResaltar = (el: TourElemento, texto: string) => () => void

/**
 * Efecto de resaltado POR DEFECTO: overlay (outline) + tooltip encima del ancla,
 * SIN tocar los estilos del host permanentemente (elementos propios que se
 * remueven en el cleanup). El texto del paso se pinta con `textContent` —
 * JAMÁS `innerHTML`: cero HTML del catálogo al DOM. Scroll al ancla si el
 * entorno lo soporta.
 */
export const resaltarPorDefecto: EfectoResaltar = (el, texto) => {
  const doc = el.ownerDocument
  const body = doc.body
  const rect = el.getBoundingClientRect?.() ?? { top: 0, left: 0, width: 0, height: 0 }

  const overlay = doc.createElement("div")
  overlay.setAttribute("data-iris-tour", "overlay")
  Object.assign(overlay.style, {
    position: "fixed",
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    outline: "3px solid #f97316",
    outlineOffset: "2px",
    borderRadius: "6px",
    pointerEvents: "none",
    zIndex: "2147483600",
  })

  const tooltip = doc.createElement("div")
  tooltip.setAttribute("data-iris-tour", "tooltip")
  // NUNCA innerHTML: el texto del catálogo entra como texto plano, inerte.
  tooltip.textContent = texto
  Object.assign(tooltip.style, {
    position: "fixed",
    top: `${rect.top + rect.height + 8}px`,
    left: `${rect.left}px`,
    maxWidth: "280px",
    padding: "8px 12px",
    background: "#111827",
    color: "#f9fafb",
    borderRadius: "8px",
    font: "14px/1.4 system-ui, sans-serif",
    zIndex: "2147483601",
  })

  body.appendChild(overlay)
  body.appendChild(tooltip)
  el.scrollIntoView?.({ block: "center", inline: "nearest" })

  return () => {
    overlay.remove()
    tooltip.remove()
  }
}

// --- Motor ------------------------------------------------------------------

export interface MotorToursOpts {
  catalogo: CatalogoTours
  /** Documento del host (default `globalThis.document`). */
  documento?: TourDocumento
  /** Página VIVA del host (pathname) para chequear aplicabilidad del tour. */
  paginaActual: () => string
  /** Emite un `tour_evento` hacia el shell (lo envuelve el bridge en el sobre). */
  emitirEvento: (payload: TourEventoPayload) => void
  /** Efecto de resaltado (default {@link resaltarPorDefecto}). */
  resaltarEfecto?: EfectoResaltar
  /**
   * ESPERA ACOTADA del ancla en el auto-avance por click: cuando la acción del
   * paso monta el DOM del siguiente ancla de forma asíncrona (modal de Vue tras
   * `await import()`, flush de reactividad), el ancla no está en el mismo tick
   * del click. Se reintenta el `querySelector` hasta `intentos` veces, con
   * `intervaloMs` entre intentos, antes de emitir `ancla_no_encontrada`. Default
   * 40×50ms (~2s). `programar` (default `setTimeout`/`clearTimeout`, devuelve su
   * cancelador) es inyectable para el harness (control determinista del reloj).
   */
  espera?: {
    intentos?: number
    intervaloMs?: number
    programar?: (fn: () => void, ms: number) => () => void
  }
}

interface Activo {
  tourId: string
  tour: Tour
  paso: number
  limpiar: (() => void) | null
  desconectar: (() => void) | null
}

type ResultadoPaso =
  | { ok: true }
  | { ok: false; codigo: string; detalle: ErrorPuenteDetalle }

/** Construye el motor de tours sobre un catálogo YA validado. */
export function crearMotorTours(opts: MotorToursOpts): MotorTours {
  const documento =
    opts.documento ?? (globalThis as unknown as { document?: TourDocumento }).document
  const resaltar = opts.resaltarEfecto ?? resaltarPorDefecto
  const porId = new Map(opts.catalogo.tours.map((t) => [t.id, t]))

  // --- Espera acotada del ancla (auto-avance sobre DOM montado async) ---------
  const esperaIntentos = opts.espera?.intentos ?? 40
  const esperaIntervalo = opts.espera?.intervaloMs ?? 50
  const programar =
    opts.espera?.programar ??
    ((fn: () => void, ms: number): (() => void) => {
      const id = setTimeout(fn, ms)
      return () => clearTimeout(id)
    })

  let activo: Activo | null = null
  /** Cancelador del reintento en vuelo (una espera a la vez). */
  let esperaPendiente: (() => void) | null = null

  const emitir = (paso: number, hito: TourEventoPayload["hito"], tourId: string): void => {
    opts.emitirEvento({ tourId, paso, hito })
  }

  const cancelarEspera = (): void => {
    if (!esperaPendiente) return
    esperaPendiente()
    esperaPendiente = null
  }

  const limpiarActivo = (): void => {
    cancelarEspera()
    if (!activo) return
    activo.desconectar?.()
    activo.limpiar?.()
    activo = null
  }

  const aplicaEnPagina = (tour: Tour): boolean => {
    if (!tour.paginas) return true // sin `paginas` = corre en cualquier página
    const actual = normalizarPagina(opts.paginaActual())
    return tour.paginas.some((p) => normalizarPagina(p) === actual)
  }

  /**
   * Busca el ancla del paso en el DOM. El ancla ya pasó el pattern anti-selector
   * al validar el catálogo: el selector se construye AQUÍ, jamás sale del
   * catálogo crudo.
   */
  const buscarAncla = (ancla: string): TourElemento | null =>
    documento ? documento.querySelector(`[data-iris="${ancla}"]`) : null

  /**
   * Pinta el paso `n` sobre un ancla YA resuelta: limpia el paso previo (y
   * cualquier espera en vuelo), resalta/scrollea/pinta el tooltip, arma la espera
   * de la acción esperada, marca `activo` y emite `paso_mostrado`.
   *
   * `function` (no arrow) por la recursión mutua con `avanzarInterno`: ambas se
   * hoistean, así el auto-avance por click puede llamarse hacia adelante.
   */
  function presentarPaso(tour: Tour, n: number, el: TourElemento): void {
    limpiarActivo()
    const pasoDef = tour.pasos[n - 1]
    const limpiar = resaltar(el, pasoDef.texto)

    let desconectar: (() => void) | null = null
    if (pasoDef.accionEsperada?.tipo === "click") {
      const onClick = (): void => {
        emitir(n, "paso_completado", tour.id)
        avanzarInterno(tour, n + 1)
      }
      el.addEventListener("click", onClick)
      desconectar = () => el.removeEventListener("click", onClick)
    }

    activo = { tourId: tour.id, tour, paso: n, limpiar, desconectar }
    emitir(n, "paso_mostrado", tour.id)
  }

  /**
   * Pinta el paso `n` (1-based). Camino SÍNCRONO de los comandos del wire
   * (`correr`/`avanzar`/`resaltar`): el ancla debe estar en el DOM AHORA — si no,
   * `ancla_no_encontrada` (tipado + evento) inmediato. La espera acotada del
   * auto-avance vive en {@link avanzarInterno}, no aquí.
   */
  function mostrarPaso(tour: Tour, n: number): ResultadoPaso {
    const pasoDef = tour.pasos[n - 1]
    if (!documento) {
      return { ok: false, codigo: "error_interno", detalle: { tourId: tour.id, paso: n } }
    }
    const el = buscarAncla(pasoDef.ancla)
    if (!el) {
      limpiarActivo()
      emitir(n, "ancla_no_encontrada", tour.id)
      return {
        ok: false,
        codigo: "ancla_no_encontrada",
        detalle: { tourId: tour.id, paso: n, ancla: pasoDef.ancla, paginaActual: opts.paginaActual() },
      }
    }
    presentarPaso(tour, n, el)
    return { ok: true }
  }

  /**
   * ESPERA ACOTADA del ancla tras un auto-avance por click: reintenta el
   * `querySelector` hasta `esperaIntentos` veces (cada `esperaIntervalo` ms). Es
   * la respuesta al race demostrado: el click abre un modal que monta el ancla
   * del siguiente paso tras un `await import()` + flush de Vue, así que el ancla
   * NO está en el mismo tick del click. Si el ancla aparece dentro de la ventana,
   * pinta el paso; si se agota, degrada limpio a `ancla_no_encontrada`.
   *
   * `activo` sigue apuntando al paso previo durante la espera (el resaltado
   * anterior persiste y `cancelar`/`destruir` pueden abortarla).
   */
  function esperarYMostrar(tour: Tour, n: number): void {
    cancelarEspera() // no acumular esperas (p.ej. re-click del ancla ya completada)
    const pasoDef = tour.pasos[n - 1]
    let restantes = esperaIntentos
    const intentar = (): void => {
      esperaPendiente = null
      const el = buscarAncla(pasoDef.ancla)
      if (el) {
        presentarPaso(tour, n, el)
        return
      }
      if (restantes-- <= 0) {
        limpiarActivo()
        emitir(n, "ancla_no_encontrada", tour.id)
        return
      }
      esperaPendiente = programar(intentar, esperaIntervalo)
    }
    esperaPendiente = programar(intentar, esperaIntervalo)
  }

  /**
   * Auto-avance por click: intenta pintar el siguiente paso en el MISMO tick
   * (DOM ya presente → cero latencia, comportamiento previo) y, si el ancla aún
   * no está montada, cae en la espera acotada. `tour_avanzar` (manual) NO pasa
   * por aquí: usa el camino síncrono de `avanzar`.
   */
  function avanzarInterno(tour: Tour, siguiente: number): void {
    if (siguiente > tour.pasos.length) {
      limpiarActivo()
      emitir(tour.pasos.length, "tour_terminado", tour.id)
      return
    }
    const el = buscarAncla(tour.pasos[siguiente - 1].ancla)
    if (el) {
      presentarPaso(tour, siguiente, el)
      return
    }
    esperarYMostrar(tour, siguiente)
  }

  const correr = (payload: TourCorrerPayload): ResultadoTour => {
    const tour = porId.get(payload.tourId)
    if (!tour) {
      return { ok: false, codigo: "tour_desconocido", detalle: { disponibles: [...porId.keys()] } }
    }
    if (!aplicaEnPagina(tour)) {
      return {
        ok: false,
        codigo: "pagina_no_aplica",
        detalle: { paginasDelTour: tour.paginas, paginaActual: opts.paginaActual() },
      }
    }
    const paso = payload.paso ?? 1
    if (paso < 1 || paso > tour.pasos.length) {
      return { ok: false, codigo: "paso_invalido", detalle: { tourId: tour.id, paso } }
    }
    const res = mostrarPaso(tour, paso)
    if (!res.ok) return res
    return { ok: true, payload: { tourId: tour.id, paso, pasosTotal: tour.pasos.length } }
  }

  const avanzar = (payload: TourAvanzarPayload): ResultadoTour => {
    if (!activo || activo.tourId !== payload.tourId) {
      return { ok: false, codigo: "tour_inactivo", detalle: { tourId: payload.tourId } }
    }
    const tour = activo.tour
    const pasosTotal = tour.pasos.length
    // Destino primero: un `paso` inválido es error tipado SIN tocar el estado
    // del tour vigente — jamás emitir `paso_completado` por un destino que no
    // se va a alcanzar.
    if (payload.paso < 1) {
      return { ok: false, codigo: "paso_invalido", detalle: { tourId: tour.id, paso: payload.paso } }
    }
    // El "Siguiente" manual completa el paso vigente (ya sabemos que el
    // destino es alcanzable: dentro de rango o "más allá del último" = fin).
    emitir(activo.paso, "paso_completado", tour.id)

    if (payload.paso > pasosTotal) {
      limpiarActivo()
      emitir(pasosTotal, "tour_terminado", tour.id)
      return { ok: true, payload: { tourId: tour.id, paso: pasosTotal, pasosTotal, terminado: true } }
    }
    const res = mostrarPaso(tour, payload.paso)
    if (!res.ok) return res
    return {
      ok: true,
      payload: { tourId: tour.id, paso: payload.paso, pasosTotal, terminado: false },
    }
  }

  const cancelar = (payload: TourCancelarPayload): ResultadoTour => {
    if (!activo || activo.tourId !== payload.tourId) {
      return { ok: false, codigo: "tour_inactivo", detalle: { tourId: payload.tourId } }
    }
    emitir(activo.paso, "cancelado_por_usuario", payload.tourId)
    limpiarActivo()
    return { ok: true, payload: { tourId: payload.tourId } }
  }

  const resaltarCmd = (payload: AnclaResaltarPayload): ResultadoTour => {
    const tour = porId.get(payload.tourId)
    if (!tour) {
      return { ok: false, codigo: "tour_desconocido", detalle: { disponibles: [...porId.keys()] } }
    }
    if (payload.paso < 1 || payload.paso > tour.pasos.length) {
      return { ok: false, codigo: "paso_invalido", detalle: { tourId: tour.id, paso: payload.paso } }
    }
    // El ancla debe ser EXACTAMENTE la declarada por ese paso (garantía del
    // contrato: no existe resaltar suelto — es un tour de 1 paso).
    if (tour.pasos[payload.paso - 1].ancla !== payload.ancla) {
      return {
        ok: false,
        codigo: "ancla_fuera_de_tour",
        detalle: { tourId: tour.id, paso: payload.paso, ancla: payload.ancla },
      }
    }
    if (!aplicaEnPagina(tour)) {
      return {
        ok: false,
        codigo: "pagina_no_aplica",
        detalle: { paginasDelTour: tour.paginas, paginaActual: opts.paginaActual() },
      }
    }
    const res = mostrarPaso(tour, payload.paso)
    if (!res.ok) return res
    return { ok: true, payload: { tourId: tour.id, paso: payload.paso, ancla: payload.ancla } }
  }

  return {
    correr,
    avanzar,
    cancelar,
    resaltar: resaltarCmd,
    tours: () => [...porId.keys()],
    destruir: limpiarActivo,
  }
}
